import { useMemo, useCallback, useEffect } from "react"
import PropTypes from "prop-types"
import { size, map } from "lodash"
import { Paper, Divider, Skeleton } from "@mui/material"
import { FilterList } from "@mui/icons-material"
import ErrorComponent from "components/error"
import SearchableMenu from "components/searchable-menu"
import { ERROR, combineStatuses } from "helpers/data-statuses"
import { log } from "helpers/logger"
import { getInstrumentTypeCharacter } from "helpers/metrics/utils"
import { getDate } from "modules/intraday/selectors/dates"
import { useClassifications } from "resources/classifications"
import { useModelMetadata } from "resources/models"
import { useIsBattingSluggingEnabled } from "resources/account"
import { useAnalytics } from "connected-components/analytics"
import { SHARED_EVENTS } from "connected-components/analytics/shared/events"
import { useTrackDashboardModuleAdded } from "connected-components/dashboard/analytics"
import { useAddModule } from "connected-components/dashboard/hooks/use-add-module"
import {
  buildSecondaryModuleConfigFromMenuItem,
  SECONDARY_MODULE_CONFIG_VIEW_KEYS,
} from "connected-components/dashboard/helpers/module-renderers"
import { MODULE_TYPES } from "connected-components/dashboard/modules/types"
import { getOpenInNewTabMergeOptions, mergeItemsWithOrderAndSecondaryAction } from "connected-components/dashboard/components/view-selectors/assets-menu-items"
import { usePositionSetBetaInstrument } from "connected-components/asset-view/helpers"
import { useInstrumentsWithQueryOptions, useModeInstruments, useAvailableGroupByOptions, SELECTED_MODE_VALUES, useBaseWithSegment } from "connected-components/dashboard/helpers"
import { useIntradayState } from "connected-components/dashboard/hooks/use-intraday-state"
import { RULES } from "connected-components/compare/position-sets"
import { useSyncKPIPreferences } from "connected-components/dashboard/hooks/use-sync-kpi-preferences"
import { useKPIData } from "./hooks/use-kpi-data"
import { KPIAnalyticsProvider, KPI_EVENTS, useKPILoadedTracking } from "./analytics"
import Header from "./components/header"
import summaryTemplate from "./templates/summary/index"
import performanceTemplate from "./templates/performance/index"
import decisionsTemplate from "./templates/decisions/index"

import "./styles.scss"

/**
 * Enhances an instrument with tag and loading information
 * @param {Object} instrument - The instrument to enhance
 * @param {string} tag - The tag to apply (e.g., "O", "R", "C")
 * @param {boolean} hasMultipleInstruments - Whether multiple instruments are present
 * @returns {Object} Enhanced instrument with tag, isLoading, and displayName properties
 */
function enhanceInstrument(instrument, tag, shouldShowTag, instrumentType, isCompareWeightStrategy) {
  // Check if this specific instrument is loading/missing data
  const instrumentIsLoading = !instrument?.name
  const instrumentName = instrument?.name || "Loading..."

  if (isCompareWeightStrategy && instrumentType === "comparison") {
    return {
      ...instrument,
      tag: null,
      isLoading: instrumentIsLoading,
      name: "Selection",
      displayName: "Selection",
    }
  }

  return {
    ...instrument,
    tag: shouldShowTag ? tag : null,
    isLoading: instrumentIsLoading,
    displayName: shouldShowTag
      ? `${tag}: ${instrumentIsLoading ? "Loading..." : instrumentName}`
      : instrumentName,
  }
}

function useEnhancedModeInstruments(instrumentsWithMetadata, selectedMode, isEndVsStartDateComparison, isPerformance, isCompareWeightStrategy) {
  const { original: originalInstrumentWithMetadata, comparison: comparisonInstrumentWithMetadata, rebalanced: rebalancedInstrumentWithMetadata } = instrumentsWithMetadata
  const hasMultipleInstruments = size(instrumentsWithMetadata) > 1

  // Create enhanced instruments with tag and loading information
  const enhancedInstruments = useMemo(() => {
    const originalTag = getInstrumentTypeCharacter("original", isEndVsStartDateComparison, isCompareWeightStrategy)
    const rebalancedTag = getInstrumentTypeCharacter("rebalanced", isEndVsStartDateComparison, isCompareWeightStrategy)
    const comparisonTag = getInstrumentTypeCharacter("comparison", isEndVsStartDateComparison, isCompareWeightStrategy, comparisonInstrumentWithMetadata?.isOptimization)

    return [
      enhanceInstrument(originalInstrumentWithMetadata, originalTag, hasMultipleInstruments, "original", isCompareWeightStrategy),
      enhanceInstrument(rebalancedInstrumentWithMetadata, rebalancedTag, hasMultipleInstruments, "rebalanced", isCompareWeightStrategy),
      enhanceInstrument(comparisonInstrumentWithMetadata, comparisonTag, hasMultipleInstruments, "comparison", isCompareWeightStrategy),
    ]
  }, [originalInstrumentWithMetadata, rebalancedInstrumentWithMetadata, comparisonInstrumentWithMetadata, hasMultipleInstruments, isEndVsStartDateComparison, isCompareWeightStrategy])

  // Use the original useModeInstruments logic to get the correct order
  return useModeInstruments(...enhancedInstruments, selectedMode, isPerformance)
}

export default function KPIWrapper(props) {
  const { initialTab, ...rest } = props
  // Seed initialTab into the slice BEFORE the sync runs: useKPIData owns the (idempotent) seed, and
  // calling it ahead of useSyncKPIPreferences registers the seed effect first (hook-call order), so
  // initFromModuleConfig lands before the legacy "kpi" fallback load (which omits selectedTab) —
  // otherwise a panel configured with e.g. initialTab "summary" could keep the default "decisions".
  useKPIData(initialTab)
  // Per-panel preference sync (the default layout has two KPI panels). Hold the KPI tree until the
  // slice slot is loaded: KPI + its analytics provider read the slice, and before it is populated
  // useKPIData falls back to DEFAULT_KPI_STATE (selectedTab "decisions").
  const isPreferencesLoaded = useSyncKPIPreferences({ initialTab })

  if (!isPreferencesLoaded) {
    return (
      <Paper className="kpi-root" square>
        <Skeleton variant="rectangular" width="100%" height="100%" />
      </Paper>
    )
  }

  return (
    <KPIAnalyticsProvider>
      <KPI {...rest} />
    </KPIAnalyticsProvider>
  )
}

KPIWrapper.propTypes = {
  initialTab: PropTypes.string,
}

export function KPI(props) {
  const { instrumentsWithMetadata } = props
  const {
    modelId,
    startDate,
    endDate,
    instrument: original,
    secondInstrument: rebalanced,
    comparisonInstrument: comparison,
    selectedMode,
    normalization,
    filter,
    isExpanded,
    base,
    isEndVsStartDateComparison,
    performanceEndDate,
    instrumentsMetadata,
    selectedTab,
    setSelectedTab,
  } = useKPIData()
  const addModule = useAddModule()
  const isBattingSluggingEnabled = useIsBattingSluggingEnabled()

  const analytics = useAnalytics()
  const trackModuleAdded = useTrackDashboardModuleAdded()
  useKPILoadedTracking()

  const TEMPLATES = useMemo(() => ({
    summary: summaryTemplate,
    performance: performanceTemplate,
    ...(isBattingSluggingEnabled && { decisions: decisionsTemplate }),
  }), [isBattingSluggingEnabled])

  const rawMenuItems = useMemo(
    () =>
      [
        { id: "performance", label: "Performance", groupId: "kpi", groupLabel: "KPIs" },
        { id: "summary", label: "Summary", groupId: "kpi", groupLabel: "KPIs" },
        isBattingSluggingEnabled && { id: "decisions", label: "Decisions", groupId: "kpi", groupLabel: "KPIs" },
      ].filter(Boolean),
    [isBattingSluggingEnabled],
  )

  const handleOpenInNewTab = useCallback(
    (item, event) => {
      const config = buildSecondaryModuleConfigFromMenuItem(item, event, SECONDARY_MODULE_CONFIG_VIEW_KEYS.INITIAL_TAB)
      addModule({
        type: MODULE_TYPES.KPI,
        config,
      })
      trackModuleAdded(MODULE_TYPES.KPI, config)
    },
    [addModule, trackModuleAdded],
  )

  const menuItems = useMemo(
    () =>
      mergeItemsWithOrderAndSecondaryAction([rawMenuItems], getOpenInNewTabMergeOptions(handleOpenInNewTab)),
    [rawMenuItems, handleOpenInNewTab],
  )

  const [modelMetadata, modelMetadataStatus] = useModelMetadata(modelId)
  const [classifications, classificationsStatus] = useClassifications()

  const instrumentOptions = { isExpanded, normalization, filter }
  const instrumentsWithQueryOptions = useInstrumentsWithQueryOptions({ original, rebalanced, comparison }, selectedMode, instrumentOptions)
  const betaInstrument = usePositionSetBetaInstrument(instrumentsWithMetadata.original, modelMetadata)
  const [availableGroups, availableGroupsStatus] = useAvailableGroupByOptions(instrumentsWithMetadata.original, endDate, { isActive: Boolean(base), hasMultipleInstruments: false })
  const baseWithSegment = useBaseWithSegment(base, filter, normalization)

  const isPerformance = ["performance", "decisions"].includes(selectedTab)

  const isCompareWeightStrategy = useMemo(
    () => instrumentsWithMetadata?.comparison?.type === RULES,
    [instrumentsWithMetadata],
  )

  const enhancedInstruments = useEnhancedModeInstruments(instrumentsWithMetadata, selectedMode, isEndVsStartDateComparison, isPerformance, isCompareWeightStrategy)
  const [{ isIntradayMode }] = useIntradayState()

  // Not in a memo to cause a re-render when the date changes
  const intradayDate = getDate()
  const currentDate = useMemo(() => isIntradayMode ? intradayDate : endDate, [isIntradayMode, intradayDate, endDate])
  const getEffectiveEndDate = useCallback((index) => {
    if (isPerformance) {
      return performanceEndDate
    }
    if (!isEndVsStartDateComparison) {
      return currentDate
    }
    if (selectedMode === SELECTED_MODE_VALUES.start) {
      return startDate
    }
    return index === 1 ? startDate : currentDate
  }, [isEndVsStartDateComparison, startDate, currentDate, selectedMode, isPerformance, performanceEndDate])

  // Compute position sets for the current mode
  const { original: originalWithOptions, rebalanced: rebalancedWithOptions, comparison: comparisonWithOptions } = instrumentsWithQueryOptions
  const positionSets = useModeInstruments(originalWithOptions, rebalancedWithOptions, comparisonWithOptions, selectedMode, isPerformance)

  // Create standard position sets with query options that all templates can use
  const positionSetsWithQueryOptions = useMemo(() =>
    map(positionSets, set => ({
      ...set,
      queryPositionSet: set,
      queryBase: baseWithSegment,
    })), [positionSets, baseWithSegment],
  )

  const showDelta = positionSets.length === 2

  const status = combineStatuses([modelMetadataStatus, classificationsStatus, availableGroupsStatus])

  const getLabel = useCallback((itemLabel) => {
    return filter ? <>
      <span style={{ marginRight: "0.5rem" }}><FilterList /></span>
      <span>{itemLabel}</span>
    </> : itemLabel
  }, [filter])

  // Reset to summary if the selected tab doesn't have a template
  useEffect(() => {
    if (!TEMPLATES[selectedTab]) {
      log(new Error(`KPI - Template not found for selected tab: ${selectedTab}`))
      setSelectedTab("summary")
    }
  }, [selectedTab, setSelectedTab, TEMPLATES])

  if (status === ERROR) {
    analytics.track(SHARED_EVENTS.ERROR, {
      errorType: "data_loading_error",
      errorMessage: "Failed to load KPI data",
    })
    return <ErrorComponent />
  }

  // Fallback to summary template if the current tab doesn't have a template
  const template = TEMPLATES[selectedTab] || summaryTemplate

  const templateProps = {
    ...props,
    modelMetadata,
    betaInstrument,
    classifications,
    instrumentsWithQueryOptions,
    instrumentsWithMetadata,
    availableGroups,
    getEffectiveEndDate,
    currentDate,
    positionSets,
    positionSetsWithQueryOptions,
    isIntradayMode,
    showDelta,
    instrumentsMetadata,
  }

  const content = <Paper className="kpi-root" square style={{ display: "flex", flexDirection: "column" }}>
    <div className="kpi-header">
      <SearchableMenu
        items={menuItems}
        current={selectedTab}
        onSelect={(selectedItem) => {
          analytics.track(KPI_EVENTS.TAB_SELECTED, { tabName: selectedItem.id })
          setSelectedTab(selectedItem.id)
        }}
        getLabel={getLabel}
      />
      {template.Header && <template.Header />}
    </div>
    <Divider />

    <Header instruments={enhancedInstruments} />
    <div className="kpi-content">
      <template.Component {...templateProps} />
    </div>
  </Paper>

  if (template.Context) {
    return <template.Context>
      {content}
    </template.Context>
  }

  return content
}

KPI.propTypes = {
  instrumentsWithMetadata: PropTypes.shape({
    original: PropTypes.object,
    rebalanced: PropTypes.object,
    comparison: PropTypes.object,
    base: PropTypes.object,
  }).isRequired,
}
